import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List, ListOrdered,
  Link2, Palette, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Trash2,
} from 'lucide-react';

/**
 * Lightweight rich-text editor for email templates.
 *
 * Uses the browser's `contenteditable` + `document.execCommand` API for the
 * formatting actions. Yes, execCommand is technically deprecated, but every
 * major browser still supports it and it sidesteps a heavyweight dep like
 * Tiptap/Quill (which would add ~120kB to the bundle for a single editor).
 *
 * Stores the raw HTML in `value`; emits it on every change via `onChange`.
 * The variable chips insert their literal token (e.g. `{{client.name}}`) at
 * the caret position and the parent re-renders the editor with the new HTML.
 */
const COLORS = ['#1A1B2E', '#4F46E5', '#0F766E', '#B45309', '#B91C1C', '#0369A1', '#6B6C85'];

export default function RichEmailEditor({ value, onChange, variables = [] }) {
  const ref = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  // Initialize to a sentinel that's never equal to a real HTML value, so the
  // first effect run always pushes the parent's value into the DOM.
  const lastValue = useRef(null);

  // Only push HTML into the DOM when the parent's value diverges from what we
  // last emitted — prevents caret jumps while the user is typing.
  useEffect(() => {
    if (!ref.current) return;
    if ((value || '') !== lastValue.current) {
      ref.current.innerHTML = value || '';
      lastValue.current = value || '';
    }
  }, [value]);

  const exec = useCallback((cmd, arg = null) => {
    if (!ref.current) return;
    ref.current.focus();
    document.execCommand(cmd, false, arg);
    const html = ref.current.innerHTML;
    lastValue.current = html;
    onChange?.(html);
  }, [onChange]);

  const handleInput = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastValue.current = html;
    onChange?.(html);
  };

  const insertAtCursor = (token) => {
    if (!ref.current) return;
    ref.current.focus();
    // Wrap variable in a chip span so it's visible in the editor — but inline
    // it as plain text in the saved HTML so the backend just substitutes.
    document.execCommand('insertText', false, token);
    const html = ref.current.innerHTML;
    lastValue.current = html;
    onChange?.(html);
  };

  const insertLink = () => {
    const url = prompt('URL (https://…) ?');
    if (!url) return;
    exec('createLink', url);
  };

  const clearAll = () => {
    if (!window.confirm('Effacer tout le contenu ?')) return;
    if (ref.current) {
      ref.current.innerHTML = '';
      lastValue.current = '';
      onChange?.('');
    }
  };

  return (
    <div className="rounded-xl bg-white border border-[#EFF0F6] overflow-hidden focus-within:border-[#4F46E5] focus-within:ring-2 focus-within:ring-[#EDE8FF] transition-colors" data-testid="rich-email-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#EFF0F6] bg-[#FAFAFE]">
        <ToolBtn onClick={() => exec('bold')} title="Gras (Ctrl+B)"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Italique (Ctrl+I)"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('underline')} title="Souligné (Ctrl+U)"><UnderlineIcon className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec('formatBlock', 'H2')} title="Titre 2"><Heading2 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', 'H3')} title="Titre 3"><Heading3 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', 'P')} title="Paragraphe">¶</ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Liste à puces"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Liste numérotée"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec('justifyLeft')} title="Aligner à gauche"><AlignLeft className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyCenter')} title="Centrer"><AlignCenter className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyRight')} title="Aligner à droite"><AlignRight className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={insertLink} title="Insérer un lien"><Link2 className="w-3.5 h-3.5" /></ToolBtn>
        <div className="relative">
          <ToolBtn onClick={() => setShowColorPicker((s) => !s)} title="Couleur du texte"><Palette className="w-3.5 h-3.5" /></ToolBtn>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 z-10 bg-white border border-[#EFF0F6] rounded-lg shadow-lg p-1.5 flex gap-1">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { exec('foreColor', c); setShowColorPicker(false); }}
                  className="w-5 h-5 rounded-full border border-[#EFF0F6] hover:scale-110 transition-transform"
                  style={{ background: c }}
                  title={c}
                />
              ))}
            </div>
          )}
        </div>
        <Sep />
        <ToolBtn onClick={() => exec('undo')} title="Annuler (Ctrl+Z)"><Undo2 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('redo')} title="Rétablir (Ctrl+Y)"><Redo2 className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={clearAll} title="Tout effacer" className="text-[#B91C1C] hover:bg-[#FEF2F2]"><Trash2 className="w-3.5 h-3.5" /></ToolBtn>

        <div className="flex-1" />

        {variables.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap py-0.5">
            <span className="text-[10.5px] uppercase tracking-wide text-[#8E8FA5] font-semibold mr-1">Variables :</span>
            {variables.map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`var-chip-${v}`}
                onClick={() => insertAtCursor(v)}
                className="px-2 py-0.5 rounded-md bg-[#EEF2FF] text-[#4F46E5] text-[10.5px] font-mono hover:bg-[#E8E2FD] transition-colors"
                title={`Insérer ${v} au curseur`}
              >
                {v}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        ref={ref}
        contentEditable
        data-testid="rich-email-editor-area"
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        className="min-h-[280px] max-h-[480px] overflow-y-auto px-4 py-3 text-[13.5px] text-[#1A1B2E] leading-relaxed outline-none rich-editor-content"
        style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
      />
      <style>{`
        .rich-editor-content h2 { font-size: 18px; font-weight: 700; color: #1A1B2E; margin: 8px 0 6px; line-height: 1.3; }
        .rich-editor-content h3 { font-size: 15px; font-weight: 700; color: #1A1B2E; margin: 6px 0 4px; line-height: 1.4; }
        .rich-editor-content p { margin: 0 0 8px; }
        .rich-editor-content ul, .rich-editor-content ol { margin: 0 0 8px; padding-left: 22px; }
        .rich-editor-content ul li { list-style: disc; }
        .rich-editor-content ol li { list-style: decimal; }
        .rich-editor-content a { color: #4F46E5; text-decoration: underline; }
        .rich-editor-content:empty:before { content: 'Tapez votre message ici…'; color: #B5B6C9; }
      `}</style>
    </div>
  );
}

function ToolBtn({ children, onClick, title, className = '' }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`w-7 h-7 rounded flex items-center justify-center text-[#4A4B63] hover:bg-[#EEF2FF] hover:text-[#4F46E5] text-[12px] font-bold transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-[#EFF0F6] mx-0.5" />;
}
